import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiCheck,
  FiExternalLink,
  FiMail,
  FiMoon,
  FiSun,
  FiTrash2,
  FiXCircle,
} from 'react-icons/fi';
import { deleteJobSeekerAccount } from '../account/deleteJobSeekerAccount';
import { getCachedFirebaseValue, setCachedDataValue } from '../firebaseDataCache';
import {
  disconnectEmailProvider,
  getEmailIntegrationStatus,
  startEmailProviderConnection,
} from '../profile/emailIntegrations';
import { useLanguage } from '../contexts/LanguageContext';
import useLanguagePreference from '../profile/useLanguagePreference';
import {
  getStoredTheme,
  setThemePreference,
  THEMES,
} from '../themePreference';

const TERMS_URL = 'https://app.termly.io/policy-viewer/policy.html?policyUUID=fdbb78f0-13a4-4e26-8308-1615683c9865';
const PRIVACY_URL = 'https://app.termly.io/policy-viewer/policy.html?policyUUID=0dcb12c9-7703-4262-8b93-37cfc29a2ecc';

function SettingsScreen({ user, profile, onProfileSaved }) {
  const { language, t } = useLanguage();
  const emailCacheKey = user?.uid ? `api/${user.uid}/email-integrations` : '';
  const cachedIntegrations = emailCacheKey ? getCachedFirebaseValue(emailCacheKey) : undefined;
  const [searchParams] = useSearchParams();
  const [confirmation, setConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [emailIntegrations, setEmailIntegrations] = useState(cachedIntegrations || []);
  const [emailLoading, setEmailLoading] = useState(Boolean(user) && cachedIntegrations === undefined);
  const [emailBusy, setEmailBusy] = useState('');
  const [emailFeedback, setEmailFeedback] = useState('');
  const { localeBusy, localeFeedback, handleLanguageChange } = useLanguagePreference({ user, profile, onProfileSaved });
  const [theme, setTheme] = useState(getStoredTheme);
  const canDelete = confirmation.trim().toUpperCase() === 'DELETE';
  const connectedProvider = useMemo(
    () => emailIntegrations.find((integration) => integration.connected),
    [emailIntegrations],
  );
  const isLightTheme = theme === THEMES.LIGHT;

  const handleThemeChange = () => {
    const nextTheme = isLightTheme ? THEMES.DARK : THEMES.LIGHT;
    setTheme(setThemePreference(nextTheme));
  };

  useEffect(() => {
    let mounted = true;

    const loadEmailStatus = async () => {
      if (!user) return;
      const hasCachedIntegrations = getCachedFirebaseValue(emailCacheKey) !== undefined;
      setEmailLoading(!hasCachedIntegrations);
      try {
        const response = await getEmailIntegrationStatus(user);
        if (mounted) {
          const integrations = response.integrations || [];
          setCachedDataValue(emailCacheKey, integrations);
          setEmailIntegrations(integrations);
        }
      } catch (error) {
        console.error('Failed to load email integration status:', error);
        if (mounted) setEmailFeedback(error.message);
      } finally {
        if (mounted) setEmailLoading(false);
      }
    };

    loadEmailStatus();

    return () => {
      mounted = false;
    };
  }, [user, emailCacheKey]);

  useEffect(() => {
    const connected = searchParams.get('email_connected');
    const provider = searchParams.get('email_provider');

    if (connected === '1') {
      setEmailFeedback(`${provider === 'outlook' ? 'Outlook' : 'Gmail'} ${t('settings.email.connectedForOutreach')}`);
    } else if (connected === '0') {
      setEmailFeedback(t('settings.email.connectionFailed'));
    }
  }, [searchParams, t]);

  const refreshEmailStatus = async () => {
    const response = await getEmailIntegrationStatus(user);
    const integrations = response.integrations || [];
    setCachedDataValue(emailCacheKey, integrations);
    setEmailIntegrations(integrations);
  };

  const handleConnectEmail = async (provider) => {
    setEmailBusy(provider);
    setEmailFeedback('');
    try {
      const response = await startEmailProviderConnection(user, provider);
      window.location.assign(response.authUrl);
    } catch (error) {
      console.error('Failed to start email OAuth:', error);
      setEmailFeedback(error.message);
      setEmailBusy('');
    }
  };

  const handleDisconnectEmail = async (provider) => {
    setEmailBusy(provider);
    setEmailFeedback('');
    try {
      await disconnectEmailProvider(user, provider);
      await refreshEmailStatus();
      setEmailFeedback(t('settings.email.disconnected'));
    } catch (error) {
      console.error('Failed to disconnect email:', error);
      setEmailFeedback(error.message);
    } finally {
      setEmailBusy('');
    }
  };

  const handleDeleteAccount = async () => {
    if (!canDelete || deleting) return;

    setDeleting(true);
    setFeedback('');

    try {
      await deleteJobSeekerAccount({ user });
    } catch (error) {
      console.error('Failed to delete account:', error);
      setFeedback(
        error?.code === 'auth/requires-recent-login'
          ? t('settings.danger.recentLogin')
          : t('settings.danger.failed'),
      );
      setDeleting(false);
    }
  };

  return (
    <section className="js-settings" aria-labelledby="settings-title">
      <header className="js-settings__head">
        <Link to="/profile" className="js-settings__back js-secondary-back">
          <FiArrowLeft className="js-icon--directional" aria-hidden="true" />
          <span>{t('settings.backToProfile')}</span>
        </Link>
        <h1 id="settings-title" className="js-section-title">{t('settings.title')}</h1>
      </header>

      <section className="js-settings-card js-settings-appearance" aria-labelledby="settings-appearance-title">
        <div className="js-settings-appearance__intro">
          <span className="js-settings-appearance__icon" aria-hidden="true">
            {isLightTheme ? <FiSun /> : <FiMoon />}
          </span>
          <div>
            <h2 id="settings-appearance-title">{t('settings.appearance.title')}</h2>
            <p>{t('settings.appearance.description')}</p>
          </div>
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={isLightTheme}
          aria-label={isLightTheme
            ? t('settings.appearance.switchToDark')
            : t('settings.appearance.switchToLight')}
          className={`js-theme-switch ${isLightTheme ? 'is-light' : ''}`}
          onClick={handleThemeChange}
        >
          <span className="js-theme-switch__mode">
            {isLightTheme ? t('settings.appearance.light') : t('settings.appearance.dark')}
          </span>
          <span className="js-theme-switch__track" aria-hidden="true">
            <FiMoon className="js-theme-switch__moon" />
            <FiSun className="js-theme-switch__sun" />
            <i />
          </span>
        </button>
      </section>

      <section className="js-settings-card js-settings-language" aria-labelledby="settings-language-title">
        <div className="js-settings-card__head">
          <div>
            <h2 id="settings-language-title">{t('settings.language.title')}</h2>
            <p>{t('settings.language.description')}</p>
          </div>
        </div>

        <div className="js-settings-language__options" role="radiogroup" aria-labelledby="settings-language-title">
          {[
            {
              code: 'en',
              name: t('settings.language.englishName'),
              nativeName: t('settings.language.englishNative'),
            },
            {
              code: 'ar',
              name: t('settings.language.arabicName'),
              nativeName: t('settings.language.arabicNative'),
            },
          ].map((option) => {
            const selected = language === option.code;
            const saving = localeBusy === option.code;
            return (
              <button
                key={option.code}
                type="button"
                role="radio"
                aria-checked={selected}
                className={`js-settings-language__option ${selected ? 'is-selected' : ''}`}
                onClick={() => handleLanguageChange(option.code)}
                disabled={Boolean(localeBusy)}
              >
                <span className="js-settings-language__copy">
                  <strong>{option.name}</strong>
                  <small lang={option.code}>{option.nativeName}</small>
                </span>
                <span className="js-settings-language__selection" aria-hidden="true">
                  {saving ? <i className="js-settings-language__spinner" /> : selected ? <FiCheck /> : null}
                </span>
                <span className="sr-only">{selected ? t('settings.language.selected') : ''}</span>
              </button>
            );
          })}
        </div>

        {(localeBusy || localeFeedback) && (
          <p className="js-settings-language__feedback" role="status">
            {localeBusy ? t('settings.language.saving') : localeFeedback}
          </p>
        )}
      </section>

      <section className="js-settings-card" aria-label={t('settings.legal.title')}>
        <h2>{t('settings.legal.title')}</h2>
        <div className="js-settings-links">
          <a href={TERMS_URL} target="_blank" rel="noreferrer">
            <span>{t('settings.legal.terms')}</span>
            <FiExternalLink aria-hidden="true" />
          </a>
          <a href={PRIVACY_URL} target="_blank" rel="noreferrer">
            <span>{t('settings.legal.privacy')}</span>
            <FiExternalLink aria-hidden="true" />
          </a>
        </div>
      </section>

      <section className="js-settings-card js-settings-email" aria-labelledby="email-integrations-title">
        <div className="js-settings-card__head">
          <div>
            <h2 id="email-integrations-title">{t('settings.email.title')}</h2>
            <p>{t('settings.email.description')}</p>
          </div>
          <span className={`js-settings-email__status ${connectedProvider ? 'is-connected' : ''}`}>
            <FiMail aria-hidden="true" />
            <span>{emailLoading ? t('settings.email.checking') : connectedProvider ? t('settings.email.connected') : t('settings.email.notConnected')}</span>
          </span>
        </div>

        <div className="js-settings-email__providers">
          {emailLoading && emailIntegrations.length === 0 ? (
            <div className="js-personnel-spinner" role="status" aria-label={t('settings.email.checkingProviders')} />
          ) : (emailIntegrations.length > 0 ? emailIntegrations : [
            { provider: 'gmail', label: 'Gmail', connected: false },
            { provider: 'outlook', label: 'Outlook', connected: false },
          ]).map((integration) => (
            <article className="js-email-provider" key={integration.provider}>
              <div>
                <strong>{integration.label}</strong>
                <span>
                  {integration.connected
                    ? integration.email || t('settings.email.connectedAccount')
                    : `${t('settings.email.connectYour')} ${integration.label}`}
                </span>
              </div>
              {integration.connected ? (
                <button
                  type="button"
                  className="is-disconnect"
                  onClick={() => handleDisconnectEmail(integration.provider)}
                  disabled={emailBusy === integration.provider}
                >
                  <FiXCircle aria-hidden="true" />
                  <span>{emailBusy === integration.provider ? t('settings.email.disconnecting') : t('settings.email.disconnect')}</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleConnectEmail(integration.provider)}
                  disabled={emailLoading || Boolean(emailBusy)}
                >
                  <FiMail aria-hidden="true" />
                  <span>{emailBusy === integration.provider ? t('settings.email.opening') : `${t('settings.email.connect')} ${integration.label}`}</span>
                </button>
              )}
            </article>
          ))}
        </div>

        {emailFeedback && <p className="js-settings-feedback">{emailFeedback}</p>}
      </section>

      <section className="js-settings-card js-settings-danger" aria-labelledby="danger-title">
        <div>
          <h2 id="danger-title">{t('settings.danger.title')}</h2>
          <p>{t('settings.danger.description')}</p>
        </div>

        <label>
          <span>{t('settings.danger.confirmation')}</span>
          <input
            type="text"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            autoComplete="off"
          />
        </label>

        <button type="button" onClick={handleDeleteAccount} disabled={!canDelete || deleting}>
          <FiTrash2 aria-hidden="true" />
          <span>{deleting ? t('settings.danger.deleting') : t('settings.danger.delete')}</span>
        </button>

        {feedback && <p className="js-settings-feedback">{feedback}</p>}
      </section>
    </section>
  );
}

export default SettingsScreen;
